import { createReducer, on } from '@ngrx/store';
import {
  box,
  Boxed,
  createFormGroupState,
  disable,
  enable,
  FormControlState,
  FormGroupState,
  markAsSubmitted,
  onNgrxForms,
  onNgrxFormsAction,
  setValue,
  SetValueAction,
  updateGroup,
  validate,
  wrapReducerWithFormStateUpdate,
} from 'ngrx-forms';
import { required } from 'ngrx-forms/validation';
import {
  AssetClassViewModel,
  BusinessUnitViewModel,
  ProjectViewModel,
} from 'src/app/shared/models/autogenerated';
import { NonNegativeFloat2DecimalsOrInteger } from 'src/app/shared/store/custom-validators/pattern-for-numbers-validator';
import { DESCRIPTION_MAX_LENGTH } from '../../workplan/workplan.constants';
import {
  DA_DELIVERABLES,
  END_OF_PHASE,
  PROJECT_DELIVERABLES,
  THROUGH_PHASE,
} from '../new-workplan-flow.constants';
import {
  clearNewWorkplan,
  createNewWorkplanForProjectSuccess,
  createNewWorkplanSuccess,
  setAssetClasses,
  setBusinessUnits,
  setCurrentPhaseId,
  setEditProjectToNewProjectDialog,
  setNewProjectAllPhases,
  setNewProjectAvailablePhases,
  setNewProjectDALead,
  updateProjectType,
} from './new-project.actions';
import { Constants } from 'src/app/shared/utils/constants';
import { validateRichTextEditor } from 'src/app/shared/utils/rich-text-editor-util';

const FORM_ID = 'ADAP_NEW_WORK_PLAN_FORM';
const LEAD_BY_LIST: string[] = [DA_DELIVERABLES, PROJECT_DELIVERABLES];
const ENGAGEMENT_TYPE_LIST: string[] = [THROUGH_PHASE, END_OF_PHASE];

export interface NewWorkplanForm {
  projectId?: string | null;
  name: string | null;
  description: string | null;
  nojv: boolean | null;
  onShore: boolean | null;
  offShore: boolean | null;
  phaseId: string | null;
  themes: Boxed<string[]>;
  businessUnitId: string | null;
  daLead: string | null;
  daManager: string | null;
  daSupports: Boxed<(string | null)[]>;
  projectContacts: Boxed<(string | null)[]>;
  plannedStartDate: string | null;
  plannedCompletionDate: string | null;
  copyFromPhase: string | null;
  copyDADeliverables: boolean;
  copyProjectDeliverables: boolean;
  copyDeliverableMappings: boolean;
  copyPeerPool: boolean;
  copyRiskAssessment: boolean;
  leadBy: string | null;
  billingRateUSD: number | null;
  daTime: number | null;
  smeMargin: number | null;
  ipimsDomain: string | null;
  userAnonymity: boolean;
  engagementDate: string | null;
  engagementType: string | null;
  assetClassId: string | null;
  chargeCode: string | null;
}

export interface NewWorkplanState {
  newWorkplanForm: FormGroupState<NewWorkplanForm>;
  allPhases: string[];
  availablePhases: string[];
  canCopyWorkplanFrom2B: boolean;
  existingProject: ProjectViewModel | null;
  createdProject: ProjectViewModel | null;
  businessUnits: BusinessUnitViewModel[] | null;
  leadByList: string[];
  engagementTypeList: string[];
  assetClasses: AssetClassViewModel[] | null;
}

function onShoreOffShoreRequired(
  onShoreControl: FormControlState<boolean | null>,
  offShoreControl: FormControlState<boolean | null>
): FormControlState<boolean | null> {
  const onShoreSelected = onShoreControl.value === true;
  const offShoreSelected = offShoreControl.value === true;

  if (!onShoreSelected && !offShoreSelected) {
    return {
      ...onShoreControl,
      isValid: false,
      errors: { onShoreOffShoreRequired: Constants.REQUIRED_FIELD_ERROR },
      isTouched: onShoreControl.isTouched || offShoreControl.isTouched,
    };
  }

  return {
    ...onShoreControl,
    isValid: true,
    errors: {},
    isTouched: true,
  };
}

const validateForm = updateGroup<NewWorkplanForm>({
  name: validate(required),
  description: (description) =>
    validateRichTextEditor(required, description, DESCRIPTION_MAX_LENGTH),
  nojv: validate(required),
  onShore: (control, state) =>
    onShoreOffShoreRequired(control, state.controls.offShore),
  themes: validate(required),
  daLead: validate(required),
  leadBy: validate(required),
  phaseId: validate(required),
  copyDeliverableMappings: (copyDeliverableMappings, newWorkplanForm) => {
    const isSubmitted =
      newWorkplanForm?.controls?.copyDADeliverables.isSubmitted ?? false;
    const disableCopyDeliverableMappings =
      isSubmitted ||
      newWorkplanForm?.controls?.copyDADeliverables.value !== true ||
      newWorkplanForm?.controls?.copyProjectDeliverables.value !== true;

    let copyDeliverableMappingsControlOutput = copyDeliverableMappings;
    if (!isSubmitted) {
      if (disableCopyDeliverableMappings) {
        copyDeliverableMappingsControlOutput = disable(
          copyDeliverableMappingsControlOutput
        );
        copyDeliverableMappingsControlOutput = setValue(
          copyDeliverableMappingsControlOutput,
          false
        );
      } else {
        copyDeliverableMappingsControlOutput = enable(
          copyDeliverableMappingsControlOutput
        );
      }
    }
    return copyDeliverableMappingsControlOutput;
  },
  billingRateUSD: validate(required, NonNegativeFloat2DecimalsOrInteger()),
  daTime: validate(required),
});

const initialState: NewWorkplanState = {
  newWorkplanForm: createFormGroupState<NewWorkplanForm>(FORM_ID, {
    projectId: null,
    name: null,
    description: null,
    nojv: null,
    phaseId: null,
    onShore: null,
    offShore: null,
    themes: box([]),
    businessUnitId: null,
    daLead: null,
    daManager: null,
    daSupports: box([]),
    projectContacts: box([]),
    plannedStartDate: null,
    plannedCompletionDate: null,
    copyFromPhase: null,
    copyDADeliverables: false,
    copyProjectDeliverables: false,
    copyDeliverableMappings: false,
    copyPeerPool: false,
    copyRiskAssessment: false,
    leadBy: DA_DELIVERABLES,
    billingRateUSD: null,
    daTime: null,
    smeMargin: null,
    ipimsDomain: null,
    userAnonymity: true,
    engagementDate: null,
    engagementType: null,
    assetClassId: null,
    chargeCode: null,
  }),
  availablePhases: [],
  canCopyWorkplanFrom2B: false,
  allPhases: [],
  existingProject: null,
  createdProject: null,
  businessUnits: [],
  leadByList: LEAD_BY_LIST,
  engagementTypeList: ENGAGEMENT_TYPE_LIST,
  assetClasses: [],
};

function getNextAvailablePhase(state: NewWorkplanState): string | undefined {
  const project = state.existingProject;
  if (project == null) {
    return undefined;
  }

  var collator = new Intl.Collator(undefined, {
    numeric: true,
    sensitivity: 'base',
  });

  const allPhasesSorted = [...state.allPhases].sort(collator.compare);

  return allPhasesSorted.find(
    (phase) =>
      allPhasesSorted.indexOf(phase) >
      (allPhasesSorted.indexOf(project.currentPhase?.phaseId!) ?? -1)
  );
}

const rawNewProjectReducer = createReducer(
  initialState,
  onNgrxForms(),
  on(clearNewWorkplan, () => initialState),

  on(setCurrentPhaseId, (state, { phaseId }) => ({
    ...state,
    newWorkplanForm: setValue(<NewWorkplanForm>{
      ...state.newWorkplanForm.value,
      phaseId: phaseId,
    })(state.newWorkplanForm),
  })),

  on(setEditProjectToNewProjectDialog, (state, { project }) => {
    return {
      ...state,
      newWorkplanForm: createFormGroupState<NewWorkplanForm>(FORM_ID, {
        ...state.newWorkplanForm.value,
        projectId: project?.id,
        name: project?.name ?? null,
        description: project?.description ?? null,
        nojv: project?.nojv ?? null,
        onShore: project?.onShore ?? null,
        offShore: project?.offShore ?? null,
        themes: box(project?.currentPhase?.themes ?? []),
        businessUnitId: project?.businessUnitId ?? null,
        daLead: project?.currentPhase?.daLead ?? null,
        daManager: project?.currentPhase?.daManager ?? null,
        daSupports: box(project?.currentPhase?.daSupports ?? []),
        projectContacts: box(project?.currentPhase?.projectContacts ?? []),
        leadBy: project?.currentPhase?.leadBy ?? null,
        ipimsDomain: project?.currentPhase?.ipimsDomain ?? null,
        assetClassId: project?.assetClassId ?? null,
        chargeCode: project?.chargeCode ?? null,
      }),
      existingProject: project,
      canCopyWorkplanFrom2B: (
        project?.phases
          .map((projectPhase) => projectPhase.phaseId)
          .filter((s): s is string => s != null) ?? []
      ).includes('2B'),
    };
  }),

  on(setNewProjectAvailablePhases, (state) => {
    const nextPhase = getNextAvailablePhase(state);
    return {
      ...state,
      newWorkplanForm: createFormGroupState<NewWorkplanForm>(FORM_ID, {
        ...state.newWorkplanForm.value,
        copyFromPhase: getCopyFromPhase(state.canCopyWorkplanFrom2B, nextPhase),
        phaseId: nextPhase ?? null,
      }),
      availablePhases: nextPhase
        ? [nextPhase]
        : getPhasesNotIncludedInTheProject(state),
    };
  }),

  on(setNewProjectDALead, (state, { daLead }) => ({
    ...state,
    newWorkplanForm: createFormGroupState<NewWorkplanForm>(FORM_ID, {
      ...state.newWorkplanForm.value,
      daLead,
    }),
  })),

  on(setBusinessUnits, (state, { businessUnits }) => ({
    ...state,
    businessUnits: businessUnits,
  })),

  on(setNewProjectAllPhases, (state, { allPhases }) => ({
    ...state,
    allPhases,
  })),

  on(createNewWorkplanSuccess, (state, { project }) => ({
    ...state,
    newWorkplanForm: disable(state.newWorkplanForm),
    createdProject: project,
  })),

  on(createNewWorkplanForProjectSuccess, (state, { project }) => ({
    ...state,
    newWorkplanForm: markAsSubmitted(disable(state.newWorkplanForm)),
    createdProject: project,
  })),

  on(updateProjectType, (state, { onShore, offShore }) => ({
    ...state,
    newWorkplanForm: updateGroup<NewWorkplanForm>({
      onShore: (control) => setValue(control, onShore),
      offShore: (control) => setValue(control, offShore),
    })(state.newWorkplanForm),
  })),

  on(setAssetClasses, (state, { assetClasses }) => ({
    ...state,
    assetClasses: assetClasses,
  })),

  onNgrxFormsAction(SetValueAction, (state, action) => {
    let newWorkplanForm = state.newWorkplanForm;

    newWorkplanForm = setValue(<NewWorkplanForm>{
      ...newWorkplanForm.value,
      copyFromPhase: getCopyFromPhase(
        state.canCopyWorkplanFrom2B,
        state.newWorkplanForm.controls?.phaseId?.value
      ),
    })(newWorkplanForm);

    if (action.controlId === `${FORM_ID}.phaseId`) {
      newWorkplanForm = setValue(<NewWorkplanForm>{
        ...newWorkplanForm.value,
        copyWorkplanFrom2B: false,
      })(newWorkplanForm);
    }

    return {
      ...state,
      newWorkplanForm: newWorkplanForm,
    };
  })
);

function getCopyFromPhase(
  canCopyFrom2B: boolean,
  phaseId: string | null | undefined
) {
  const isPhase2CSelected = phaseId === '2C';
  return isPhase2CSelected && canCopyFrom2B ? '2B' : null;
}

function getPhasesNotIncludedInTheProject(state: NewWorkplanState) {
  return state.allPhases.filter(
    (ph) =>
      !state.existingProject?.phases
        .map((projectPhase) => projectPhase.phaseId)
        .includes(ph)
  );
}

export const newWorkplanReducer = wrapReducerWithFormStateUpdate(
  rawNewProjectReducer,
  (s) => s.newWorkplanForm,
  validateForm
);
